import type { PolicyVerdict } from '@squadlab/contracts';
import { annualLeaveEntitlement } from './annual-leave.js';
import { workingDaysBetween } from './working-days.js';

/**
 * Annual-leave request check (art. 109): the requested span is costed in
 * WORKING days (weekends and KSA holidays are free), then weighed against the
 * remaining balance (entitlement − taken this year). The verdict carries all
 * four numbers so gates and the audit trail can show the arithmetic.
 */
export interface LeaveRequestInput {
  readonly serviceYears: number;
  /** Working days already taken this leave year. */
  readonly takenDays: number;
  readonly startDate: string;
  readonly endDate: string;
}

export function leaveRequestCheck(input: LeaveRequestInput): PolicyVerdict {
  const entitlement = annualLeaveEntitlement(input.serviceYears);
  const requestedWorkingDays = workingDaysBetween(input.startDate, input.endDate);
  const remainingDays = Math.max(0, entitlement.days - input.takenDays);

  const computed = {
    requestedWorkingDays,
    entitlementDays: entitlement.days,
    takenDays: input.takenDays,
    remainingDays,
  };

  if (requestedWorkingDays === 0) {
    return {
      allowed: false,
      ruleRef: entitlement.ruleRef,
      explanation: `The span ${input.startDate} → ${input.endDate} contains no working days (weekend/holiday only) — nothing to deduct.`,
      computed,
    };
  }
  if (requestedWorkingDays > remainingDays) {
    return {
      allowed: false,
      ruleRef: entitlement.ruleRef,
      explanation: `Requested ${requestedWorkingDays} working day(s) but only ${remainingDays} remain of the ${entitlement.days}-day entitlement.`,
      computed,
    };
  }
  return {
    allowed: true,
    ruleRef: entitlement.ruleRef,
    explanation: `Requested ${requestedWorkingDays} working day(s); ${remainingDays} of ${entitlement.days} remain — within entitlement.`,
    computed,
  };
}
